import { useLayoutEffect, useState } from 'react';

export type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const SPOTLIGHT_PAD = 8;

export function queryTourTarget(target: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour-target="${target}"]`);
}

export function measureSpotlight(el: HTMLElement | null): SpotlightRect | null {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    top: Math.max(8, rect.top - SPOTLIGHT_PAD),
    left: Math.max(8, rect.left - SPOTLIGHT_PAD),
    width: rect.width + SPOTLIGHT_PAD * 2,
    height: rect.height + SPOTLIGHT_PAD * 2,
  };
}

/** Keeps spotlight coordinates aligned with the tour target (resize / expand). */
export function useTourSpotlightRect(target: string | undefined, enabled: boolean): SpotlightRect | null {
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);

  useLayoutEffect(() => {
    if (!enabled || !target) {
      setSpotlight(null);
      return;
    }

    const measure = () => {
      setSpotlight(measureSpotlight(queryTourTarget(target)));
    };

    measure();

    const el = queryTourTarget(target);
    const ro = el ? new ResizeObserver(measure) : null;
    ro?.observe(el!);

    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, target]);

  return spotlight;
}
